import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Phone, Mail, Send, CheckCircle, AlertCircle, Calendar as CalendarIcon } from 'lucide-react';
import { Calendar as CalendarComponent } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    guest_name: '',
    email: '',
    phone: '',
    room_type: 'deluxe',
    num_guests: 2,
    check_in_date: null,
    check_out_date: null,
    special_requests: ''
  });

  const set = (k, v) => setFormData(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/bookings`, {
        ...formData,
        check_in_date: formData.check_in_date ? format(formData.check_in_date, 'yyyy-MM-dd') : '',
        check_out_date: formData.check_out_date ? format(formData.check_out_date, 'yyyy-MM-dd') : '',
        total_price: 0,
      });
      setSuccess(true);
      setFormData({
        guest_name: '', email: '', phone: '',
        room_type: 'deluxe', num_guests: 2,
        check_in_date: null, check_out_date: null,
        special_requests: ''
      });
      setTimeout(() => setSuccess(false), 6000);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fieldClass = "w-full bg-transparent border-0 border-b border-[#D4AF37]/30 px-0 py-3 text-[#E8E3D9] placeholder:text-[#8E9A95]/60 focus:outline-none focus:border-[#D4AF37] transition-colors text-[14px]";

  return (
    <section id="contact" className="relative bg-[#0B0F0E] py-32 md:py-44 overflow-hidden" data-testid="contact-section">

      <div className="max-w-[1480px] mx-auto px-6 md:px-12">

        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 lg:col-span-3">
            <div className="overline">Chapter VII</div>
            <div className="numeral text-7xl md:text-8xl text-[#D4AF37]/30 mt-4">07</div>
          </div>
          <div className="col-span-12 lg:col-span-9">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="font-serif text-4xl md:text-6xl lg:text-7xl font-light text-[#E8E3D9] leading-[1.05]"
            >
              Write us a <span className="italic text-[#D4AF37]">letter.</span>
            </motion.h2>
            <p className="mt-6 max-w-xl text-[#8E9A95] text-[15px] leading-relaxed">
              Send a note and we will reply in kind — usually within a day. Or call us;
              someone is always near the bell.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">

          {/* form — letter style */}
          <motion.div
            id="booking"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="lg:col-span-7 bg-[#121816] border border-[#D4AF37]/25 p-8 md:p-14"
            data-testid="booking-form"
          >
            <div className="flex items-center justify-between mb-10">
              <div>
                <div className="overline mb-2">Reservation enquiry</div>
                <h3 className="font-serif text-2xl md:text-3xl text-[#E8E3D9]">A note to the resort</h3>
              </div>
              <div className="numeral text-3xl text-[#D4AF37]/30 hidden md:block">{new Date().getFullYear()}</div>
            </div>

            {success && (
              <div className="mb-8 p-4 border border-[#D4AF37]/40 bg-[#D4AF37]/5 flex items-center gap-3">
                <CheckCircle className="text-[#D4AF37] flex-shrink-0" size={18} />
                <p className="text-[#E8E3D9] text-[13px]">Your enquiry has reached us. We'll be in touch shortly.</p>
              </div>
            )}

            <form onSubmit={submit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="overline mb-3 block">Full name</label>
                  <input
                    type="text"
                    value={formData.guest_name}
                    onChange={(e) => set('guest_name', e.target.value)}
                    required
                    placeholder="Your name"
                    className={fieldClass}
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="overline mb-3 block">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => set('phone', e.target.value)}
                    required
                    placeholder="10-digit number"
                    className={fieldClass}
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="overline mb-3 block">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => set('email', e.target.value)}
                  required
                  placeholder="your@email.com"
                  className={fieldClass}
                  data-testid="contact-email-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="overline mb-3 block">Room type</label>
                  <select
                    value={formData.room_type}
                    onChange={(e) => set('room_type', e.target.value)}
                    className={fieldClass + ' cursor-pointer appearance-none'}
                    style={{ backgroundImage: 'linear-gradient(45deg, transparent 50%, #D4AF37 50%), linear-gradient(135deg, #D4AF37 50%, transparent 50%)', backgroundPosition: 'calc(100% - 12px) center, calc(100% - 7px) center', backgroundSize: '5px 5px, 5px 5px', backgroundRepeat: 'no-repeat' }}
                    data-testid="contact-room-select"
                  >
                    <option value="deluxe" className="bg-[#121816]">Deluxe Room</option>
                    <option value="standard" className="bg-[#121816]">Standard Room</option>
                    <option value="couple" className="bg-[#121816]">Couple Room</option>
                  </select>
                </div>
                <div>
                  <label className="overline mb-3 block">Guests</label>
                  <input
                    type="number"
                    min={1}
                    value={formData.num_guests === '' ? '' : formData.num_guests}
                    onChange={(e) => { const v = e.target.value; set('num_guests', v === '' ? '' : parseInt(v) || ''); }}
                    onBlur={() => { if (formData.num_guests === '' || formData.num_guests < 1) set('num_guests', 1); }}
                    className={fieldClass}
                    data-testid="contact-guests-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="overline mb-3 block">Check-in</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={fieldClass + ' text-left flex items-center justify-between'}
                        data-testid="contact-checkin-input"
                      >
                        <span className={formData.check_in_date ? 'text-[#E8E3D9]' : 'text-[#8E9A95]/60'}>
                          {formData.check_in_date ? format(formData.check_in_date, 'PPP') : 'Select date'}
                        </span>
                        <CalendarIcon size={14} className="text-[#D4AF37]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#121816] border-[#D4AF37]/40 z-[100]" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={formData.check_in_date || undefined}
                        onSelect={(d) => set('check_in_date', d)}
                        disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="overline mb-3 block">Check-out</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={fieldClass + ' text-left flex items-center justify-between'}
                        data-testid="contact-checkout-input"
                      >
                        <span className={formData.check_out_date ? 'text-[#E8E3D9]' : 'text-[#8E9A95]/60'}>
                          {formData.check_out_date ? format(formData.check_out_date, 'PPP') : 'Select date'}
                        </span>
                        <CalendarIcon size={14} className="text-[#D4AF37]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#121816] border-[#D4AF37]/40 z-[100]" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={formData.check_out_date || undefined}
                        onSelect={(d) => set('check_out_date', d)}
                        disabled={(d) => d <= (formData.check_in_date || new Date())}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div>
                <label className="overline mb-3 block">A short note</label>
                <textarea
                  value={formData.special_requests}
                  onChange={(e) => set('special_requests', e.target.value)}
                  placeholder="Any special requirements or questions…"
                  rows={4}
                  className={fieldClass + ' resize-none'}
                  data-testid="contact-requests-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-brass w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="contact-submit-btn"
              >
                {loading ? 'Sending…' : (<><Send size={14} />Send enquiry</>)}
              </button>
            </form>
          </motion.div>

          {/* sidebar — direct contact + policy */}
          <div className="lg:col-span-5 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.2 }}
              className="space-y-px bg-[#D4AF37]/15"
            >
              <a href="tel:+918310838799" className="block bg-[#121816] p-6 md:p-7 hover:bg-[#1A2421] transition-colors group" data-testid="contact-phone-1">
                <div className="overline mb-2">Call or WhatsApp</div>
                <div className="flex items-center gap-3">
                  <Phone size={18} className="text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
                  <span className="font-serif text-2xl text-[#E8E3D9]">+91 83108 38799</span>
                </div>
              </a>
              <a href="tel:+918618285170" className="block bg-[#121816] p-6 md:p-7 hover:bg-[#1A2421] transition-colors group" data-testid="contact-phone-2">
                <div className="overline mb-2">Reservations</div>
                <div className="flex items-center gap-3">
                  <Phone size={18} className="text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
                  <span className="font-serif text-2xl text-[#E8E3D9]">+91 86182 85170</span>
                </div>
              </a>
              <a href="mailto:info@tuskermark.com" className="block bg-[#121816] p-6 md:p-7 hover:bg-[#1A2421] transition-colors group" data-testid="contact-email">
                <div className="overline mb-2">Write</div>
                <div className="flex items-center gap-3">
                  <Mail size={18} className="text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
                  <span className="font-serif text-lg md:text-xl text-[#E8E3D9] break-all">info@tuskermark.com</span>
                </div>
              </a>
            </motion.div>

            {/* restaurant timings */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.4 }}
              className="border border-[#D4AF37]/20 p-6 md:p-7"
            >
              <div className="overline mb-5">At the restaurant</div>
              {[
                { meal: 'Breakfast', time: '8:30 – 10:30' },
                { meal: 'Lunch', time: '13:30 – 15:30' },
                { meal: 'Hi-Tea', time: '16:30 – 17:30' },
                { meal: 'Dinner', time: '20:30 – 22:30' },
              ].map((t) => (
                <div key={t.meal} className="flex justify-between items-baseline py-2.5 border-b border-[#D4AF37]/10 last:border-0">
                  <span className="text-[14px] text-[#C4BFB1]">{t.meal}</span>
                  <span className="text-[13px] tracking-wider text-[#D4AF37] numeral">{t.time}</span>
                </div>
              ))}
            </motion.div>

            {/* cancellation policy */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.6 }}
              className="border border-[#D4AF37]/20 p-6 md:p-7 bg-[#121816]/50"
            >
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle size={14} className="text-[#D4AF37]" />
                <div className="overline">Cancellation</div>
              </div>
              <ul className="text-[12px] text-[#8E9A95] space-y-2 leading-relaxed">
                <li><span className="text-[#E8E3D9]">25% refund</span> if cancelled 2 weeks before check-in</li>
                <li><span className="text-[#E8E3D9]">50% refund</span> within 2 weeks & 2 working days</li>
                <li><span className="text-[#E8E3D9]">No refund</span> within 2 working days</li>
                <li>Children below 5 free · 5–10 at 50% · above 10 full charge</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
